import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { Alert } from '@material-ui/lab';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

import { useObjectivesList } from '../../../../hooks/useObjectivesList';
import { ObjectiveSelect } from '../../../../components/ObjectiveSelect';
import {
  getObjetivoFlindexConfig,
  upsertObjetivoFlindexConfig,
  resetObjetivoFlindexConfig,
  notifyFlindexConfigChanged,
  normalizeFlindexIndicador,
  FLINDEX_CONFIG_CHANGED_EVENT,
} from '../../../../services/getFlindex';

// Defaults locales por si el backend no responde con `defaults` (fallback de UI).
const LOCAL_DEFAULTS = {
  C: 1.1,
  MIN_EVENTOS: 4,
  MAX_EVENTOS: 7,
  MAX_VENTANA_MINUTOS: 0,
  UMBRAL_SEGMENTO_FINO_MIN: 0,
  ACCION_SEGMENTO_FINO: 'none',
};

const ACCIONES_FINO = ['none', 'omit', 'merge_prev', 'merge_next'];

function FlindexSectionHeader({ step, title, hint, first }) {
  return (
    <Grid item xs={12} style={{ marginTop: first ? 0 : 24 }}>
      <Typography variant="subtitle2" color="textPrimary">
        {step}. {title}
      </Typography>
      {hint && (
        <Typography
          variant="caption"
          color="textSecondary"
          display="block"
          style={{ marginTop: 4 }}
        >
          {hint}
        </Typography>
      )}
    </Grid>
  );
}

const DEFAULT_INTERVALO_MIN = 5;

function formatMinutos(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function getMaxTramoCortoMin(intervaloMinutos, cFactor) {
  const intervalo = Number(intervaloMinutos) > 0 ? Number(intervaloMinutos) : DEFAULT_INTERVALO_MIN;
  const c = Number(cFactor) > 0 ? Number(cFactor) : LOCAL_DEFAULTS.C;
  return intervalo * c;
}

function shouldWarnUmbralFino(config, intervaloMinutos) {
  const umbral = Number(config.UMBRAL_SEGMENTO_FINO_MIN);
  if (!Number.isFinite(umbral) || umbral <= 0) return false;
  if (config.ACCION_SEGMENTO_FINO === 'none') return false;
  const maxTramo = getMaxTramoCortoMin(intervaloMinutos, config.C);
  return umbral > maxTramo;
}

const fieldTooltipProps = (t, fieldKey) => ({
  title: (
    <span style={{ display: 'block', maxWidth: 360, lineHeight: 1.4 }}>
      {t(`flindex.fields.tooltips.${fieldKey}`)}
    </span>
  ),
  placement: 'top',
  arrow: true,
  enterDelay: 300,
  interactive: true,
});

const SOURCE_LABEL_META = {
  user: { color: 'success', textKey: 'flindex.source.user' },
  objective_default: { color: 'info', textKey: 'flindex.source.objective_default' },
  defaults: { color: 'warning', textKey: 'flindex.source.defaults' },
};

function applyConfigPayload(data, setConfig, setDefaults, setSource) {
  const indicador = normalizeFlindexIndicador(data?.indicador);
  const defaultsNorm = normalizeFlindexIndicador(data?.defaults) || data?.defaults;
  if (indicador) {
    setConfig({ ...LOCAL_DEFAULTS, ...indicador });
    setDefaults({ ...LOCAL_DEFAULTS, ...(defaultsNorm || {}) });
    setSource(data.source || 'defaults');
    return true;
  }
  setConfig(LOCAL_DEFAULTS);
  setDefaults(LOCAL_DEFAULTS);
  setSource('defaults');
  return false;
}

function Flindex({ defaultObjective, t }) {
  const { data: objectivesList, isLoading: isLoadingObjectives } =
    useObjectivesList({ fromStorage: true });

  const fetchSeqRef = useRef(0);
  const [objectiveId, setObjectiveId] = useState('');
  const [config, setConfig] = useState(LOCAL_DEFAULTS);
  const [defaults, setDefaults] = useState(LOCAL_DEFAULTS);
  const [source, setSource] = useState('defaults');
  const [intervaloMinutos, setIntervaloMinutos] = useState(DEFAULT_INTERVALO_MIN);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbar, setSnackbar] = useState({ severity: 'success', message: '' });

  const userId = useMemo(() => sessionStorage.getItem('user'), []);
  const token = useMemo(() => sessionStorage.getItem('key'), []);

  const umbralFinoWarn = useMemo(
    () => shouldWarnUmbralFino(config, intervaloMinutos),
    [config, intervaloMinutos]
  );

  const maxTramoCortoMin = useMemo(
    () => getMaxTramoCortoMin(intervaloMinutos, config.C),
    [intervaloMinutos, config.C]
  );

  useEffect(() => {
    if (objectivesList.length && defaultObjective) {
      setObjectiveId(defaultObjective);
    }
  }, [objectivesList, defaultObjective]);

  const fetchConfig = useCallback(
    async (objId) => {
      if (!objId || !userId || !token) return;
      const seq = ++fetchSeqRef.current;
      setLoadingConfig(true);
      try {
        const res = await getObjetivoFlindexConfig(userId, objId, token);
        const data = await res.json();
        if (seq !== fetchSeqRef.current) return;

        if (data?.status === 1 || data?.status === 5) {
          localStorage.setItem('isAuthenticated', false);
          return;
        }
        if (!res.ok || (data?.status && data.status !== 0 && data.status !== 200)) {
          throw new Error(data?.message || res.statusText);
        }

        applyConfigPayload(data, setConfig, setDefaults, setSource);
        if (data?.intervalo_minutos != null) {
          setIntervaloMinutos(Number(data.intervalo_minutos));
        }
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        console.error(err);
        setConfig(LOCAL_DEFAULTS);
        setDefaults(LOCAL_DEFAULTS);
        setSource('defaults');
        setIntervaloMinutos(DEFAULT_INTERVALO_MIN);
        setSnackbar({
          severity: 'error',
          message: t('flindex.snackbar.loadError'),
        });
        setOpenSnackbar(true);
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoadingConfig(false);
        }
      }
    },
    [userId, token, t]
  );

  useEffect(() => {
    if (objectiveId) {
      fetchConfig(objectiveId);
    } else {
      setConfig(LOCAL_DEFAULTS);
      setDefaults(LOCAL_DEFAULTS);
      setSource('defaults');
      setIntervaloMinutos(DEFAULT_INTERVALO_MIN);
    }
  }, [objectiveId, fetchConfig]);

  useEffect(() => {
    const onConfigChanged = (event) => {
      const changedId = event?.detail?.objetivoId;
      if (!changedId || !objectiveId || String(changedId) !== String(objectiveId)) {
        return;
      }
      fetchConfig(objectiveId);
    };
    window.addEventListener(FLINDEX_CONFIG_CHANGED_EVENT, onConfigChanged);
    return () => window.removeEventListener(FLINDEX_CONFIG_CHANGED_EVENT, onConfigChanged);
  }, [objectiveId, fetchConfig]);

  function handleChange(field, value) {
    setConfig((prev) => ({ ...prev, [field]: value }));
  }

  function validate() {
    const errs = [];
    if (!(Number(config.C) > 0)) errs.push('C debe ser > 0');
    if (!Number.isInteger(Number(config.MIN_EVENTOS)) || Number(config.MIN_EVENTOS) < 1)
      errs.push('MIN_EVENTOS debe ser entero >= 1');
    if (Number(config.MAX_EVENTOS) < Number(config.MIN_EVENTOS))
      errs.push('MAX_EVENTOS debe ser >= MIN_EVENTOS');
    if (Number(config.MAX_VENTANA_MINUTOS) < 0)
      errs.push('MAX_VENTANA_MINUTOS debe ser >= 0');
    if (Number(config.UMBRAL_SEGMENTO_FINO_MIN) < 0)
      errs.push('UMBRAL_SEGMENTO_FINO_MIN debe ser >= 0');
    if (!ACCIONES_FINO.includes(config.ACCION_SEGMENTO_FINO))
      errs.push('ACCION_SEGMENTO_FINO inválida');
    return errs;
  }

  async function handleSave() {
    if (!objectiveId) return;
    const errs = validate();
    if (errs.length) {
      setSnackbar({ severity: 'error', message: errs.join(' · ') });
      setOpenSnackbar(true);
      return;
    }
    if (shouldWarnUmbralFino(config, intervaloMinutos)) {
      const confirmSave = window.confirm(
        t('flindex.warn.umbralFinoSaveConfirm', {
          umbral: formatMinutos(config.UMBRAL_SEGMENTO_FINO_MIN),
          max: formatMinutos(maxTramoCortoMin),
        })
      );
      if (!confirmSave) return;
    }
    setSavingConfig(true);
    try {
      const payload = {
        C: Number(config.C),
        MIN_EVENTOS: parseInt(config.MIN_EVENTOS, 10),
        MAX_EVENTOS: parseInt(config.MAX_EVENTOS, 10),
        MAX_VENTANA_MINUTOS: parseInt(config.MAX_VENTANA_MINUTOS, 10),
        UMBRAL_SEGMENTO_FINO_MIN: Number(config.UMBRAL_SEGMENTO_FINO_MIN),
        ACCION_SEGMENTO_FINO: config.ACCION_SEGMENTO_FINO,
      };
      const res = await upsertObjetivoFlindexConfig(
        userId,
        objectiveId,
        payload,
        token
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || res.statusText);
      }
      const data = await res.json();
      applyConfigPayload(data, setConfig, setDefaults, setSource);
      notifyFlindexConfigChanged(objectiveId);
      setSnackbar({
        severity: 'success',
        message: t('flindex.snackbar.saveSuccess'),
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        severity: 'error',
        message: `${t('flindex.snackbar.saveError')}: ${err.message || ''}`,
      });
    } finally {
      setOpenSnackbar(true);
      setSavingConfig(false);
    }
  }

  async function handleReset() {
    if (!objectiveId) return;
    const confirmReset = window.confirm(t('flindex.confirm.reset'));
    if (!confirmReset) return;
    setSavingConfig(true);
    try {
      const res = await resetObjetivoFlindexConfig(userId, objectiveId, token);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || res.statusText);
      }
      const data = await res.json();
      applyConfigPayload(data, setConfig, setDefaults, setSource);
      notifyFlindexConfigChanged(objectiveId);
      setSnackbar({
        severity: 'success',
        message: t('flindex.snackbar.resetSuccess'),
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        severity: 'error',
        message: `${t('flindex.snackbar.saveError')}: ${err.message || ''}`,
      });
    } finally {
      setOpenSnackbar(true);
      setSavingConfig(false);
    }
  }

  function loadDefaultsIntoForm() {
    setConfig({ ...defaults });
  }

  const sourceMeta = SOURCE_LABEL_META[source] || SOURCE_LABEL_META.defaults;
  const sourceInfo = {
    color: sourceMeta.color,
    text: t(sourceMeta.textKey),
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={4}>
          <Typography variant="h4" component="span" style={{ lineHeight: 1.2 }}>
            {t('flindex.title')}
          </Typography>
          <Tooltip
            title={
              <span style={{ display: 'block', maxWidth: 420, lineHeight: 1.45 }}>
                {t('flindex.tooltip.overview')}
              </span>
            }
            placement="right"
            enterDelay={200}
            leaveDelay={200}
            interactive
          >
            <IconButton
              size="small"
              aria-label={t('flindex.tooltip.titleAria')}
              style={{ padding: 4, color: '#90A4AE' }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body1" style={{ marginTop: 8 }}>
          {t('flindex.subtitle')}
        </Typography>
        <Typography variant="body2" style={{ marginTop: 8 }}>
          {t('flindex.description.p1')}
        </Typography>
        <Typography variant="body2">{t('flindex.description.p2')}</Typography>
        <Typography variant="body2" style={{ marginTop: 8, color: '#546E7A' }}>
          {t('flindex.description.expectations')}
        </Typography>
      </CardContent>

      <Box padding={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item lg={4} md={4} sm={6} xs={12}>
            <ObjectiveSelect
              objetivos={objectivesList}
              objetivoId={objectiveId}
              setObjetivoId={setObjectiveId}
              isLoading={isLoadingObjectives}
            />
          </Grid>
          {objectiveId && !loadingConfig && (
            <Grid item>
              <Alert severity={sourceInfo.color}>
                {t('flindex.source.label')}: {sourceInfo.text}
              </Alert>
            </Grid>
          )}
        </Grid>

        {!objectiveId && (
          <Box marginTop={2}>
            <Alert severity="info">{t('flindex.alert.selectObjective')}</Alert>
          </Box>
        )}

        {objectiveId && loadingConfig && (
          <Box display="flex" alignItems="center" gridGap={12} marginTop={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">{t('flindex.loadingConfig')}</Typography>
          </Box>
        )}

        {objectiveId && !loadingConfig && (
          <Box marginTop={3}>
            <Grid container spacing={2}>
              <FlindexSectionHeader
                step={1}
                first
                title={t('flindex.sections.preprocess.title')}
                hint={t('flindex.sections.preprocess.hint')}
              />
              <Grid item md={6} sm={6} xs={12}>
                <Tooltip {...fieldTooltipProps(t, 'UMBRAL_SEGMENTO_FINO_MIN')}>
                  <Box width="100%">
                    <TextField
                      label={t('flindex.fields.UMBRAL_SEGMENTO_FINO_MIN')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ step: '0.1', min: '0' }}
                      value={config.UMBRAL_SEGMENTO_FINO_MIN}
                      onChange={(e) =>
                        handleChange('UMBRAL_SEGMENTO_FINO_MIN', e.target.value)
                      }
                    />
                    <FormHelperText>
                      {t('flindex.fields.helpers.UMBRAL_SEGMENTO_FINO_MIN', {
                        defaultValue: defaults.UMBRAL_SEGMENTO_FINO_MIN,
                      })}
                    </FormHelperText>
                  </Box>
                </Tooltip>
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <Tooltip {...fieldTooltipProps(t, 'ACCION_SEGMENTO_FINO')}>
                  <Box width="100%">
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel id="lbl-accion-fino">
                        {t('flindex.fields.ACCION_SEGMENTO_FINO')}
                      </InputLabel>
                      <Select
                        labelId="lbl-accion-fino"
                        label={t('flindex.fields.ACCION_SEGMENTO_FINO')}
                        value={config.ACCION_SEGMENTO_FINO}
                        onChange={(e) =>
                          handleChange('ACCION_SEGMENTO_FINO', e.target.value)
                        }
                      >
                        {ACCIONES_FINO.map((opt) => (
                          <MenuItem value={opt} key={opt}>
                            {t(`flindex.fields.acciones.${opt}`)}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {t('flindex.fields.helpers.ACCION_SEGMENTO_FINO', {
                          defaultValue: defaults.ACCION_SEGMENTO_FINO,
                        })}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                </Tooltip>
              </Grid>

              {umbralFinoWarn && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    {t('flindex.warn.umbralFinoHint', {
                      umbral: formatMinutos(config.UMBRAL_SEGMENTO_FINO_MIN),
                      max: formatMinutos(maxTramoCortoMin),
                    })}
                  </Alert>
                </Grid>
              )}

              <FlindexSectionHeader
                step={2}
                title={t('flindex.sections.detect.title')}
                hint={t('flindex.sections.detect.hint')}
              />
              <Grid item md={6} sm={6} xs={12}>
                <Tooltip {...fieldTooltipProps(t, 'C')}>
                  <Box width="100%">
                    <TextField
                      label={t('flindex.fields.C')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ step: '0.01', min: '0.01' }}
                      value={config.C}
                      onChange={(e) => handleChange('C', e.target.value)}
                    />
                    <FormHelperText>
                      {t('flindex.fields.helpers.C', { defaultValue: defaults.C })}
                    </FormHelperText>
                  </Box>
                </Tooltip>
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <Tooltip {...fieldTooltipProps(t, 'MAX_EVENTOS')}>
                  <Box width="100%">
                    <TextField
                      label={t('flindex.fields.MAX_EVENTOS')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ step: '1', min: '1' }}
                      value={config.MAX_EVENTOS}
                      onChange={(e) => handleChange('MAX_EVENTOS', e.target.value)}
                    />
                    <FormHelperText>
                      {t('flindex.fields.helpers.MAX_EVENTOS', {
                        defaultValue: defaults.MAX_EVENTOS,
                      })}
                    </FormHelperText>
                  </Box>
                </Tooltip>
              </Grid>

              <FlindexSectionHeader
                step={3}
                title={t('flindex.sections.validate.title')}
                hint={t('flindex.sections.validate.hint')}
              />
              <Grid item md={6} sm={6} xs={12}>
                <Tooltip {...fieldTooltipProps(t, 'MIN_EVENTOS')}>
                  <Box width="100%">
                    <TextField
                      label={t('flindex.fields.MIN_EVENTOS')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ step: '1', min: '1' }}
                      value={config.MIN_EVENTOS}
                      onChange={(e) => handleChange('MIN_EVENTOS', e.target.value)}
                    />
                    <FormHelperText>
                      {t('flindex.fields.helpers.MIN_EVENTOS', {
                        defaultValue: defaults.MIN_EVENTOS,
                      })}
                    </FormHelperText>
                  </Box>
                </Tooltip>
              </Grid>
              <Grid item md={6} sm={6} xs={12}>
                <Tooltip {...fieldTooltipProps(t, 'MAX_VENTANA_MINUTOS')}>
                  <Box width="100%">
                    <TextField
                      label={t('flindex.fields.MAX_VENTANA_MINUTOS')}
                      type="number"
                      fullWidth
                      variant="outlined"
                      size="small"
                      inputProps={{ step: '1', min: '0' }}
                      value={config.MAX_VENTANA_MINUTOS}
                      onChange={(e) =>
                        handleChange('MAX_VENTANA_MINUTOS', e.target.value)
                      }
                    />
                    <FormHelperText>
                      {t('flindex.fields.helpers.MAX_VENTANA_MINUTOS', {
                        defaultValue: defaults.MAX_VENTANA_MINUTOS,
                      })}
                    </FormHelperText>
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginTop={3}
              gridGap={8}
              flexWrap="wrap"
            >
              <Button
                variant="outlined"
                color="default"
                onClick={loadDefaultsIntoForm}
                disabled={savingConfig}
              >
                {t('flindex.actions.loadDefaults')}
              </Button>

              <Box display="flex" gridGap={8} flexWrap="wrap">
                {/* {source === 'user' && (
                  <Button
                    variant="outlined"
                    style={{ borderColor: '#d13636', color: '#d13636' }}
                    onClick={handleReset}
                    disabled={savingConfig}
                  >
                    {t('flindex.actions.reset')}
                  </Button>
                )} */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={savingConfig}
                >
                  {savingConfig
                    ? t('flindex.actions.saving')
                    : t('flindex.actions.save')}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Card>
  );
}

export default withRouter(withTranslation()(Flindex));
